import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ReportGrid } from './ReportGrid';
import { ReportGridDataset, ReportGridProps } from './types';
import { buildReportGridModel } from './core/model';
import { defaultReportGridSettings, ReportGridSettings, ReportGridSettingsForm, settingsInitialExpansion } from './ReportGridSettingsForm';

/** JSON persistível. O sistema consumidor define a estrutura da configuração de dados. */
export interface ReportGridDefinition<Configuration> {
  version: 1;
  configuration: Configuration;
  settings: ReportGridSettings;
  dataset: ReportGridDataset;
}
export interface ReportGridPanelProps<Configuration> {
  /** Sem valor: inicia a criação. Com valor: renderiza imediatamente, sem buscar dados. */
  initialValue?: ReportGridDefinition<Configuration>;
  createConfiguration: Configuration;
  initialSettings?: ReportGridSettings;
  renderDataControls: (configuration: Configuration, onChange: (next: Configuration) => void) => ReactNode;
  canApply?: (configuration: Configuration) => boolean;
  /** Chamado na criação ou ao alterar a configuração de dados. */
  onGenerate: (configuration: Configuration, signal: AbortSignal) => Promise<ReportGridDataset>;
  /** Conecte à persistência do sistema. Falhas mantêm o rascunho e a tabela anterior. */
  onSave?: (definition: ReportGridDefinition<Configuration>, signal: AbortSignal) => Promise<void> | void;
  onChange?: (definition: ReportGridDefinition<Configuration>) => void;
  settingsPresentation?: 'inline' | 'modal';
  gridProps?: Omit<ReportGridProps, 'dataset' | 'controls' | 'onRequestSettings'>;
}
// Os botões do painel devem levar seus estilos para qualquer projeto/modal.
const ActionButton = styled.button<{ $primary?: boolean }>`
  font: inherit; font-weight: 600; line-height: 1.4;
  padding: 11px 20px; border-radius: 7px; cursor: pointer;
  border: 1px solid ${({ $primary }) => $primary ? '#14675e' : '#cadbd0'};
  background: ${({ $primary }) => $primary ? '#14675e' : '#f7faf7'};
  color: ${({ $primary }) => $primary ? '#fff' : '#14675e'};
  &:hover:not(:disabled) { background: ${({ $primary }) => $primary ? '#10564f' : '#edf3ed'}; }
  &:focus-visible { outline: 3px solid #d3a73b; outline-offset: 2px; }
  &:disabled { cursor: default; opacity: .5; }
`;
const Editor = styled.div`
  padding: 16px; border: 1px solid #dfe8e4; border-radius: 10px; background: #fff; color: #213c37;
  .report-grid-editor-actions { display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-end; margin-top: 12px; }
  .report-grid-editor-error { color: #953d32; padding: 12px; background: #fff0ed; }
`;
const Dialog = styled.dialog`
  width: min(1200px, calc(100% - 32px)); max-height: 90vh; overflow: auto;
  font: inherit; color: #213c37; padding: 0; border: 1px solid #cadbd0; border-radius: 12px;
  &::backdrop { background: #123c3980; }
`;
/** Reusable creation/edit/view shell. No API, routing, database, or source format is assumed. */
export function ReportGridPanel<Configuration>({ initialValue, createConfiguration, initialSettings,
  renderDataControls, canApply, onGenerate, onSave, onChange, settingsPresentation = 'inline', gridProps = {},
}: ReportGridPanelProps<Configuration>) {
  const [saved, setSaved] = useState(initialValue);
  const [configuration, setConfiguration] = useState(initialValue?.configuration ?? createConfiguration);
  const [settings, setSettings] = useState(initialValue?.settings ?? initialSettings ?? defaultReportGridSettings);
  const [editing, setEditing] = useState(!initialValue);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const request = useRef<AbortController | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const mode = gridProps.mode ?? 'edit';
  const canEdit = mode === 'edit' && (gridProps.canEdit ?? true);
  useEffect(() => () => request.current?.abort(), []);
  useEffect(() => {
    if (editing && canEdit && settingsPresentation === 'modal') {
      if (!dialog.current?.open) dialog.current?.showModal();
    } else if (dialog.current?.open) dialog.current.close();
  }, [editing, canEdit, settingsPresentation]);
  useEffect(() => { if (!canEdit) { request.current?.abort(); setBusy(false); setEditing(false); } }, [canEdit]);
  const begin = () => {
    trigger.current = document.activeElement as HTMLElement;
    setConfiguration(saved?.configuration ?? createConfiguration);
    setSettings(saved?.settings ?? initialSettings ?? defaultReportGridSettings);
    setError(''); setEditing(true);
  };
  const cancel = () => {
    request.current?.abort(); setBusy(false); setError(''); setEditing(false);
    trigger.current?.focus();
  };
  const apply = async () => {
    if (busy || !canEdit || canApply?.(configuration) === false) return;
    const controller = new AbortController(); request.current = controller;
    setBusy(true); setError('');
    try {
      // Configurações devem ser serializáveis em JSON para persistência e comparação.
      const changed = !saved || JSON.stringify(configuration) !== JSON.stringify(saved.configuration);
      const dataset = changed ? await onGenerate(configuration, controller.signal) : saved.dataset;
      if (controller.signal.aborted) return;
      buildReportGridModel(dataset);
      const next: ReportGridDefinition<Configuration> = { version: 1, configuration, settings, dataset };
      await onSave?.(next, controller.signal);
      if (controller.signal.aborted) return;
      setSaved(next); setEditing(false); onChange?.(next); trigger.current?.focus();
    } catch (failure) {
      if (!controller.signal.aborted) setError((failure as Error).message || 'Não foi possível aplicar as configurações.');
    } finally { if (!controller.signal.aborted) setBusy(false); }
  };
  const editor = editing && canEdit ? <Editor aria-label="Editor da tabela">
    <h2>Configurações da tabela</h2>
    <ReportGridSettingsForm value={settings} onChange={setSettings} disabled={busy}
      dataControls={renderDataControls(configuration, setConfiguration)} />
    {error && <p role="alert" className="report-grid-editor-error">{error}</p>}
    <div className="report-grid-editor-actions">
      <ActionButton type="button" onClick={cancel}>Cancelar</ActionButton>
      <ActionButton $primary type="button" disabled={busy || canApply?.(configuration) === false} onClick={apply}>
        {busy ? 'Aplicando…' : saved ? 'Aplicar' : 'Gerar relatório'}
      </ActionButton>
    </div>
  </Editor> : null;
  return <>
    {saved && <ReportGrid {...gridProps} key={`${saved.settings.initial}`} dataset={saved.dataset}
      mode={mode} canEdit={canEdit} height={saved.settings.height}
      initialExpansion={settingsInitialExpansion(saved.settings.initial)} search={saved.settings.search}
      density={saved.settings.density} appearance={saved.settings.appearance}
      title={{ ...saved.settings.title, text: saved.settings.title.text || undefined }}
      showRowTotals={saved.settings.showRowTotals} showColumnTotals={saved.settings.showColumnTotals}
      showBalance={saved.settings.showBalance} onRequestSettings={begin}
      onDensityChange={density => {
        const next = { ...saved, settings: { ...saved.settings, density } };
        setSaved(next); onChange?.(next); gridProps.onDensityChange?.(density);
      }} />}
    {!saved && !editing && <p>Nenhum relatório configurado. {canEdit && <ActionButton $primary type="button" onClick={begin}>Configurar tabela</ActionButton>}</p>}
    {settingsPresentation === 'inline' && editor}
    <Dialog ref={dialog} aria-label="Configurações da tabela" onCancel={event => { event.preventDefault(); cancel(); }}
      onClose={() => { if (settingsPresentation === 'modal' && editing) cancel(); }}>
      {settingsPresentation === 'modal' && editor}
    </Dialog>
  </>;
}
